import template from 'lodash/template';
import RateYo from 'rateyo';
import 'rateyo/lib/es/rateyo.min.css';
import { RateDialog } from '../../common/RateDialog';
import { renderRatingStars, renderReadOnlyRating } from '../../common/rating';
import formatDate from '../../common/formatDate';
import {
    AppNamespace,
    DispensaryLocation,
    PageOptions,
    Review,
    User,
} from '../../types';
import CommonDialog from '../../common/Dialog';
import { identify, peopleSet } from '../../common/action_recorder';
import formatAddress from '../../common/formatAddress';
import queryString from '../../common/queryString';
import track from '../track';
import DispensaryMenu from './DispensaryMenu';
import {
    retrieveLocation,
    retrieveMenuItems,
    retrieveReviews,
} from './dispensaryUtils';

declare const W: AppNamespace;

class DispensaryInfo {
    businessId: number;
    locationId: number;
    strainId: number | null;
    location!: DispensaryLocation;
    dialog: any;
    urls: Record<string, string>;
    isAuthenticated: boolean;
    user;

    constructor(options: PageOptions) {
        this.businessId = parseInt(
            (<HTMLInputElement>document.getElementById('business_id')).value
        );
        this.locationId = parseInt(
            (<HTMLInputElement>document.getElementById('location_id')).value
        );
        this.strainId = parseInt(
            (<HTMLInputElement>document.getElementById('strain_id')).value
        );

        this.dialog = null;

        this.urls = {
            favorite: '/api/v1/businesses/{0}/locations/{1}/favorite',
            review: '/api/v1/businesses/{0}/locations/{1}/reviews',
            menuUpdateRequest:
                '/api/v1/businesses/{0}/locations/{1}/menu-update-requests',
        };

        this.isAuthenticated = options.authenticated;
        this.user = options.user;

        retrieveLocation(this.businessId, this.locationId).then(
            ({ location }) => {
                this.location = location;
                this.showHeader();
                this.showContent();
            }
        );
    }

    priceIndicator(avg_price: number) {
        if (avg_price < 31) return 1;
        else if (avg_price < 41) return 2;
        else if (avg_price < 51) return 3;
        else if (avg_price < 66) return 4;
        else return 5;
    }

    showHeader() {
        const qs = queryString(),
            search = qs['search'];

        const headerTemplate = document.getElementById(
            'dispensary_header_template'
        )?.innerHTML;
        const headerRegion = document.getElementById('header-region');

        if (headerRegion) {
            headerRegion.innerHTML = template(headerTemplate)({
                from_search: search,
                l: this.location,
                avg_price: this.priceIndicator(this.location.avg_price),
                formatAddressLine: this.formatAddressLine,
                getOpenDays: this.getOpenDays,
                is_authenticated: AUTHENTICATED,
            });
        }

        const ratingSelector = document.getElementById('location-rating-stars');
        const rating = ratingSelector?.innerText;
        if (rating && rating !== 'Not Rated') {
            renderReadOnlyRating(<HTMLElement>ratingSelector, {
                rating: parseInt(rating),
            });
        } else {
            renderReadOnlyRating(<HTMLElement>ratingSelector, { rating: 0 });
        }

        const avgPriceLevel = this.priceIndicator(this.location.avg_price);
        this.populateAvgPriceIndicator(avgPriceLevel);
        this.clickRateLink();

        this.updateFavoritesState(this.location.is_favorite);
        this.registerAddToFavoritesHandler();
        this.registerHoursExpandLink();
        this.clickGetDirectionsBtn();
        this.clickAvgPriceIndicator();
    }

    populateAvgPriceIndicator(avgPriceLevel: number) {
        const $avgPriceIndicatorPoints = $('#avg-price-indicator').children(
            '.point'
        );

        $avgPriceIndicatorPoints.each(function (index) {
            if (index < avgPriceLevel) {
                $(this).addClass('bg-green');
            }
        });
    }

    formatAddressLine(location: DispensaryLocation) {
        const parts = [];

        if (location.city) {
            parts.push(location.city);
        }

        if (location.state) {
            parts.push(location.state);
        }

        if (location.zip_code) {
            parts.push(location.zip_code);
        }

        return parts.join(', ');
    }

    getOpenDays(location: DispensaryLocation) {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const todayIndex = new Date().getDay(); // 0 - Sun, 6 - Sat

        return [...days.slice(todayIndex), ...days.slice(0, todayIndex)].map(
            (day) => {
                const open_time =
                    location[
                        `${day.toLowerCase()}_open` as keyof DispensaryLocation
                    ];
                const close_time =
                    location[
                        `${day.toLowerCase()}_close` as keyof DispensaryLocation
                    ];

                return {
                    day,
                    time:
                        open_time && close_time
                            ? `${open_time} - ${close_time}`
                            : 'Closed',
                };
            }
        );
    }

    clickRateLink() {
        $('#add-rate-btn').on('click', function (e) {
            e.preventDefault();
            RateDialog($('.rate-location-dialog'));
        });
    }

    submitLocationReview() {
        $('.rate-location-form').on('submit', (e) => {
            e.preventDefault();
            const $form = $(e.currentTarget);
            const ratingEl = e.currentTarget.querySelector('.rate-stars');
            const rating = RateYo(ratingEl).rating();
            const $review = $form.find('.rate-review');
            const review = $review.val() as string;
            if (rating === 0) {
                $form.find('.error-message').text('Rating is required');
                return;
            }
            if (
                review &&
                review.length >
                    (parseInt($review.attr('maxlength') as string) || 500)
            ) {
                $('.error-message').text(
                    'Review max length ' + $review.attr('maxlength') ||
                        500 + ' is exceeded'
                );
                return;
            }
            const $loader = $form.find('.loader');
            const $submitBtn = $form.find('.btn-review-submit');
            $loader.removeClass('hidden');
            $submitBtn.addClass('hidden');
            $.ajax({
                type: 'POST',
                url: this.urls.review.format(
                    this.businessId.toString(),
                    this.locationId.toString()
                ),
                data: JSON.stringify({ rating: rating, review: review }),
                success: function () {
                    $loader.addClass('hidden');
                    $submitBtn.removeClass('hidden');
                    $form.find('.review').val('');
                    if ($form.parent().hasClass('dialog'))
                        $('.rate-strain-dialog').dialog('close');
                    window.location.reload();
                },
            });
        });
    }

    updateFavoritesState(isFavorite: boolean) {
        const $addToFavoritesBtn = $('#add-to-favorites-btn');

        if (isFavorite) {
            $addToFavoritesBtn
                .addClass('bg-white-smoke')
                .removeClass('border-grey')
                .addClass('border-white-smoke')
                .children('span')
                .text('Remove from Favorites');
        } else {
            $addToFavoritesBtn
                .removeClass('bg-white-smoke')
                .addClass('bg-white')
                .addClass('border-grey')
                .removeClass('border-white-smoke')
                .children('span')
                .text('Add to Favorites');
        }
    }

    registerAddToFavoritesHandler() {
        const addToFavoritesBtn = document.getElementById(
            'add-to-favorites-btn'
        );
        if (!addToFavoritesBtn) return;
        addToFavoritesBtn.addEventListener('click', () => {
            const isFavorite = this.location.is_favorite;

            this.favoriteLocation({ like: !isFavorite }).then(() => {
                this.location.is_favorite = !isFavorite;
                this.updateFavoritesState(!isFavorite);
            });
        });
    }

    favoriteLocation(data: { like: boolean }) {
        return $.ajax({
            method: 'POST',
            url: this.urls.favorite.format(
                this.businessId.toString(),
                this.locationId.toString()
            ),
            dataType: 'json',
            data: JSON.stringify(data),
        });
    }

    showPhoneDialog() {
        CommonDialog(
            $('.phone-dialog'),
            function () {
                $('.btn-close-phone-dialog').off('click');
            },
            { height: 'auto' }
        );

        $('.dialog-phone-message').text(`Call: ${this.location.phone}`);
        $('.btn-close-phone-dialog').on('click', () => {
            $('.phone-dialog').dialog('close');
        });

        track({
            event: 'DISP_CALL',
            entity_id: this.businessId,
        });
    }

    registerHoursExpandLink() {
        const $expandHoursLink = $('#expand-hours-link');
        const $otherDays = $('#other-days');

        function updateExpandButton(isExpanded: boolean) {
            for (const icon of $expandHoursLink.children('i')) {
                if (isExpanded) {
                    icon.classList.remove('fa-caret-up');
                    icon.classList.add('fa-caret-down');
                } else if (!isExpanded) {
                    icon.classList.remove('fa-caret-down');
                    icon.classList.add('fa-caret-up');
                }
            }
        }

        $expandHoursLink.on('click', function () {
            const isExpanded = $otherDays.css('display') !== 'none';

            $otherDays.slideToggle();
            updateExpandButton(isExpanded);
        });
    }

    clickAvgPriceIndicator() {
        const avgPriceIndicator = document.getElementById('avg-price');

        avgPriceIndicator?.addEventListener('click', () => {
            CommonDialog($('.avg-price-dialog'), null, {
                height: 'auto',
            });
        });
    }

    clickGetDirectionsBtn() {
        const getDirectionsBtn = document.getElementById('btn-get-directions');

        getDirectionsBtn?.addEventListener('click', () => {
            const q = formatAddress(this.location);

            track({
                event: 'DISP_GETDIR',
                entity_id: this.businessId,
            });

            if (q) {
                const url = `http://maps.google.com/?daddr=${q}?sadds=`;
                window.open(url);
            }
        });
    }

    async showContent() {
        const [menuItems, rawReviews] = await Promise.all([
            retrieveMenuItems(this.businessId, this.locationId),
            retrieveReviews(this.businessId, this.locationId),
        ]);

        const reviews = this.preFormatReviews(rawReviews.reviews);

        const contentTemplate = document.getElementById(
            'dispensary_content_template'
        )?.innerHTML;
        const contentRegion = document.getElementById('content-region');

        if (contentRegion) {
            contentRegion.innerHTML = template(contentTemplate)({
                l: this.location,
                reviews,
            });
        }

        if (menuItems.length > 0) {
            DispensaryMenu(menuItems);
        }

        this.renderReviews(reviews, 3);
        this.registerShowAllReviewsLinks(reviews);

        this.postShowContent();
    }

    postShowContent() {
        const that = this;

        $.each($('.review-wrapper'), function () {
            const $el = $(this),
                $rating = $el.find('.rating'),
                $review = $el.find('.display-text');

            renderReadOnlyRating($rating[0], {
                rating: parseInt($rating.text()),
            });
            that.changeReviewText($review);
        });

        this.expandReviewText();
        this.initRateStarsRegion();
        this.submitLocationReview();

        $('#btn-request').on('click', this.onBtnRequestClick.bind(this));
        $('#match-info').on('click', this.showMatchInfoDialog.bind(this));
    }

    initRateStarsRegion() {
        const $rateStrainForm = $('#rate-location-form');
        const $rateStarsRegion = $rateStrainForm.find('.rate-stars');
        if ($rateStarsRegion && $rateStarsRegion.length > 0) {
            renderRatingStars($rateStarsRegion[0], {
                onSet: function () {
                    const $errorMessageRegion =
                        $rateStrainForm.find('.error-message');
                    if ($errorMessageRegion) {
                        $errorMessageRegion.text('');
                    }
                },
            });
        }
    }

    postUpdateRequest(url: string) {
        const message = $('#request-message').val() as string;
        const notify = $('#request-notify').is(':checked');

        const payload = {
            message: message.trim(),
            send_notification: notify,
        };

        return $.ajax({
            method: 'POST',
            url: url,
            dataType: 'json',
            data: JSON.stringify(payload),
        });
    }

    login() {
        const email = $('#login').val(),
            password = $('#password').val();

        return $.ajax({
            method: 'POST',
            url: '/api/v1/users/login',
            dataType: 'json',
            data: JSON.stringify({ email, password }),
            success: (data) => {
                const user = data.user;
                if (user) {
                    identify(user.id);
                    peopleSet({
                        $first_name: user.first_name,
                        $last_name: user.last_name,
                        $last_login: new Date(),
                        $email: user.email,
                        account_type: user.type,
                    });

                    this.user.firstName = user.first_name;

                    if (user.geo_location) {
                        this.user.city = user.geo_location.city;
                    }
                }
                W.pages.Common.ajaxSetup();

                this.showMenuUpdateRequestDialog();
            },
            error: function (error) {
                if (error.status === 400) {
                    $('#menu-update-request-login-dialog .error-message').text(
                        JSON.parse(error.responseText).error
                    );
                }
            },
        });
    }

    changeReviewText($review: JQuery) {
        const reviewHeight = $review.height(),
            fontSize = parseInt($review.css('font-size'), 10);

        if (typeof reviewHeight === 'number' && reviewHeight / fontSize >= 2) {
            const reviewText =
                $review.text().slice(0, 65) +
                '... <span class="expander">Review full review</span>';
            $review.html(reviewText);
        }
    }

    expandReviewText() {
        $('.expander').on('click', function () {
            const parent = $(this).parent().parent();
            parent.find('.display').addClass('hidden');
            parent.find('.full').removeClass('hidden');
        });
    }

    registerShowAllReviewsLinks(reviews: Review[]) {
        const reviewLinks = ['all-reviews-link', 'all-reviews-link-mobile'].map(
            (sel) => document.getElementById(sel)
        );

        if (reviews.length <= 3) {
            hideLinks();
        }

        function hideLinks() {
            for (const link of reviewLinks) {
                link?.classList.add('hidden');
                link?.classList.remove('md:block');
            }
        }

        for (const link of reviewLinks) {
            link?.addEventListener('click', () => {
                this.renderReviews(reviews);
                hideLinks();
            });
        }
    }

    renderReviews(reviews: Review[], max?: number) {
        const reviewTemplate = template(
            document.getElementById('review_template')?.innerHTML
        );
        const reviewsRegion = document.querySelector('.reviews-wrapper');
        if (!reviewsRegion) {
            return;
        }
        reviewsRegion.innerHTML = '';

        const fragment = document.createDocumentFragment();

        for (const review of reviews.slice(0, max)) {
            const el = document.createElement('div');
            el.innerHTML = reviewTemplate({ review });
            const ratingEl = el.querySelector('.rating');
            renderReadOnlyRating(<HTMLElement>ratingEl, {
                rating: review.rating,
            });
            fragment.append(el.firstElementChild as Element);
        }

        reviewsRegion.append(fragment);
    }

    preFormatReviews(reviews: Review[]) {
        return reviews.map((review) => {
            return {
                ...review,
                review: review.review ? review.review : '(No review written)',
                created_date: formatDate(review.created_date),
            };
        });
    }

    closeDialog() {
        if (this.dialog) {
            this.dialog.dialog('close');
        }
    }

    onBtnRequestClick() {
        if (this.isAuthenticated) {
            this.showMenuUpdateRequestDialog();
        } else {
            this.showMenuUpdateRequestLoginDialog();
        }
    }

    showMenuUpdateRequestDialog() {
        const url = this.urls.menuUpdateRequest.format(
                this.businessId,
                this.locationId
            ),
            $dialogBtn = $('#btn-request-update'),
            $requestBtn = $('#btn-request');

        this.closeDialog();
        $dialogBtn.text('Request an Update');

        this.dialog = MenuUpdateRequestDialog(this.user, this.location, () => {
            $dialogBtn.prop('disabled', true);
            $dialogBtn.text('Sending Request ...');

            this.postUpdateRequest(url).always(() => {
                $requestBtn.prop('disabled', true);
                $requestBtn.attr(
                    'title',
                    'You have recently requested a menu update'
                );

                this.showMenuUpdateRequestOkDialog();
            });
        });
    }

    showMenuUpdateRequestOkDialog() {
        this.closeDialog();
        this.dialog = Dialog(
            '#menu-update-request-ok-dialog',
            '#menu-update-request-ok-dialog #btn-close',
            { title: 'Report out of stock' },
            this.closeDialog.bind(this)
        );
    }

    showMenuUpdateRequestLoginDialog() {
        this.closeDialog();
        this.dialog = Dialog(
            '#menu-update-request-login-dialog',
            '#btn-sign-in',
            {},
            this.login.bind(this)
        );
    }

    showMatchInfoDialog() {
        this.closeDialog();
        this.dialog = Dialog(
            '#match-info-dialog',
            '',
            { classes: {}, title: 'Match Percentage' },
            this.closeDialog.bind(this)
        );
    }
}

function Dialog(
    dialogSelector: string,
    btnSelector: string,
    props: {},
    onConfirm: () => void,
    btnCancel?: string
) {
    const $dialog = $(dialogSelector);

    const width = window.innerWidth > 450 ? 450 : window.innerWidth;

    $dialog.removeClass('hidden');

    const defaultProps = {
        classes: {},
        closeOnEscape: true,
        height: 'auto',
        width,
        modal: true,
        draggable: false,
        resizable: false,
        title: 'Request Menu Update',
        position: { my: 'center' },
        create: function () {
            $(this).css('max-width', '450px');
            $(this).css('min-height', 'auto');
        },
    };

    $dialog.dialog({ ...defaultProps, ...props });

    $(btnSelector).off('click');
    $(btnSelector).on('click', onConfirm);
    if (btnCancel) {
        $dialog
            .find(btnCancel)
            .off('click')
            .on('click', function () {
                $dialog.dialog('close');
            });
    }
    $(':focus').trigger('blur');
    $dialog.trigger('focus');

    return $dialog;
}

function MenuUpdateRequestDialog(
    user: User,
    location: DispensaryLocation,
    onConfirm: () => void
) {
    const dialog = Dialog(
        '#menu-update-request-dialog',
        '#btn-request-update',
        {},
        onConfirm
    );
    const defaultMessage = [
        user.city
            ? `From ${user.firstName} in ${user.city}:`
            : `From ${user.firstName}:`,
        '',
        location.location_name + ',',
        'Please update your StrainRx menu.',
        'Thank you!',
    ];

    $('#menu-update-request-dialog textarea').val(defaultMessage.join('\n'));

    return dialog;
}

new DispensaryInfo(pageOptions);
